import { Search, ListFilter, Filter, X } from "lucide-react";
import { useRef, useState, useEffect, useMemo } from "react";
import TransferModal from "./components/TransferModal";
import { useSelector } from "react-redux";
import { getAllStockTransferToDownlineAgents } from "../../../services/stockTransferToDownlineAgentsService.js";
import { handleSetSearch } from "../../../utils/searchDebouncing.js";
import ARHCustomDataTable from "../../../components/dataTable/ARHCustomDataTable.jsx";
import { useTranslation } from "react-i18next";
import StockTransferReceipt from "./components/StockTransferReceipt.jsx";
import ARHAdvanceFilterModal from "../../../components/application/date-picker/FilterModal";
import { ARHCustomDateFiler } from "../../../components/application/date-picker/rangeCallernderCard";
import Badge from "../../../components/ui/badge/Badge";

function StockTransferToDownlineAgents() {
  const { t } = useTranslation();
  const searchRef = useRef(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const userInfo = useSelector((state) => state.auth.user);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);
  
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  
  const [filter, setFilter] = useState({
    search: "",
    limit: 10,
    page: 1,
    parent_agent_id: userInfo?.uid,
    'createdAt[gte]': null,
    'createdAt[lte]': null,
    status: null,
    amount_min: null,
    amount_max: null,
    totalAmount_min: null,
    totalAmount_max: null,
    commissionRate_min: null,
    commissionRate_max: null,
  });

  const fetchData = async (filters) => {
    try {
      setIsFetching(true);
      setIsError(false);
      setError(null);
      
      const cleanFilters = {};
      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
          cleanFilters[key] = filters[key];
        }
      });
      
      const result = await getAllStockTransferToDownlineAgents(cleanFilters);
      setData(result);
    } catch (err) {
      setIsError(true);
      setError(err.message || t("stockTransfer.errorLoading"));
      console.error("Fetch error:", err);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (userInfo?.uid) {
      setIsLoading(true);
      fetchData(filter);
    }
  }, [userInfo?.uid]);

  useEffect(() => {
    if (userInfo?.uid && filter.parent_agent_id) {
      fetchData(filter);
    }
  }, [filter.search, filter.page, filter.limit, filter['createdAt[gte]'], filter['createdAt[lte]'], 
      filter.status, filter.amount_min, filter.amount_max, filter.totalAmount_min, 
      filter.totalAmount_max, filter.commissionRate_min, filter.commissionRate_max]);

  useEffect(() => {
    if (userInfo?.uid && !filter.parent_agent_id) {
      setFilter(prev => ({
        ...prev,
        parent_agent_id: userInfo?.uid
      }));
    }
  }, [userInfo]);

  const handleSearchChange = (value) => {
    setFilter((prev) => ({
      ...prev,
      search: value,
      page: 1 
    }));
  };

  const closeTransferModal = () => {
    setIsTransferModalOpen(false);
  };

  const refetch = () => {
    fetchData(filter);
  };

  const handleDateRangeApply = (startDate, endDate) => {
    const formattedStartDate = startDate ? new Date(startDate).toISOString() : null;
    const formattedEndDate = endDate ? new Date(endDate).toISOString() : null;
    
    setFilter(prev => ({
      ...prev,
      'createdAt[gte]': formattedStartDate,
      'createdAt[lte]': formattedEndDate,
      page: 1,
    }));
    setIsDatePickerOpen(false);
  };

  const clearDateFilter = () => {
    setFilter(prev => {
      const newFilter = { ...prev };
      delete newFilter['createdAt[gte]'];
      delete newFilter['createdAt[lte]'];
      return { ...newFilter, page: 1 };
    });
  };

  const formatDateRange = () => {
    if (!filter['createdAt[gte]'] || !filter['createdAt[lte]']) return null;
    const start = new Date(filter['createdAt[gte]']);
    const end = new Date(filter['createdAt[lte]']);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const filterConfig = useMemo(() => [
    {
      key: 'status',
      label: t('stockTransfer.status'),
      type: 'dropdown',
      options: [
        { value: 'pending', label: t('stockTransfer.statusPending') },
        { value: 'approved', label: t('stockTransfer.statusApproved') },
        { value: 'rejected', label: t('stockTransfer.statusRejected') },
        { value: 'completed', label: t('stockTransfer.statusCompleted') },
      ],
      multiSelect: false,
    },
    {
      key: 'amount',
      label: t('stockTransfer.amountRange'),
      type: 'range',
      min: 0,
      max: 1000000,
      unit: 'AFN',
    },
    {
      key: 'totalAmount',
      label: t('stockTransfer.totalAmountRange'),
      type: 'range',
      min: 0,
      max: 1000000,
      unit: 'AFN',
    },
    {
      key: 'commissionRate',
      label: t('stockTransfer.commissionRateRange'),
      type: 'range',
      min: 0,
      max: 100,
      unit: '%',
      step: 0.1,
    },
  ], [t]);

  const initialAdvanceValues = useMemo(() => {
    return {
      status: filter.status || '',
      amount: {
        min: filter.amount_min || 0,
        max: filter.amount_max || 1000000,
      },
      totalAmount: {
        min: filter.totalAmount_min || 0,
        max: filter.totalAmount_max || 1000000,
      },
      commissionRate: {
        min: filter.commissionRate_min || 0,
        max: filter.commissionRate_max || 100,
      },
    };
  }, [filter]);

  const handleApplyAdvanceFilters = (filterValues) => {
    setFilter(prev => {
      const newFilter = { ...prev, page: 1 };
      
      if (filterValues.status) {
        newFilter.status = filterValues.status;
      } else {
        delete newFilter.status;
      }
      
      if (filterValues.amount) {
        if (filterValues.amount.min > 0) {
          newFilter.amount_min = filterValues.amount.min;
        } else {
          delete newFilter.amount_min;
        }
        
        if (filterValues.amount.max < 1000000) {
          newFilter.amount_max = filterValues.amount.max;
        } else {
          delete newFilter.amount_max;
        }
      } else {
        delete newFilter.amount_min;
        delete newFilter.amount_max;
      }
      

      if (filterValues.totalAmount) {
        if (filterValues.totalAmount.min > 0) {
          newFilter.totalAmount_min = filterValues.totalAmount.min;
        } else {
          delete newFilter.totalAmount_min;
        }
        
        if (filterValues.totalAmount.max < 1000000) {
          newFilter.totalAmount_max = filterValues.totalAmount.max;
        } else {
          delete newFilter.totalAmount_max;
        }
      } else {
        delete newFilter.totalAmount_min;
        delete newFilter.totalAmount_max;
      }
      

      if (filterValues.commissionRate) {
        if (filterValues.commissionRate.min > 0) {
          newFilter.commissionRate_min = filterValues.commissionRate.min;
        } else {
          delete newFilter.commissionRate_min;
        }
        
        if (filterValues.commissionRate.max < 100) {
          newFilter.commissionRate_max = filterValues.commissionRate.max;
        } else {
          delete newFilter.commissionRate_max;
        }
      } else {
        delete newFilter.commissionRate_min;
        delete newFilter.commissionRate_max;
      }
      
      return newFilter;
    });
    
    setIsAdvanceFilterOpen(false);
  };

  const clearAllFilters = () => {
    setFilter({
      page: 1,
      limit: 10,
      search: "",
      parent_agent_id: userInfo?.uid,
      'createdAt[gte]': null,
      'createdAt[lte]': null,
      status: null,
      amount_min: null,
      amount_max: null,
      totalAmount_min: null,
      totalAmount_max: null,
      commissionRate_min: null,
      commissionRate_max: null,
    });
    if (searchRef.current) {
      searchRef.current.value = "";
    }
  };

  const clearSearch = () => {
    setFilter(prev => ({ ...prev, search: "", page: 1 }));
    if (searchRef.current) {
      searchRef.current.value = "";
    }
  };

  const isAnyFilterApplied = useMemo(() => {
    return (
      filter.search || 
      filter['createdAt[gte]'] || 
      filter['createdAt[lte]'] || 
      filter.status || 
      filter.amount_min || 
      filter.amount_max ||
      filter.totalAmount_min ||
      filter.totalAmount_max ||
      filter.commissionRate_min ||
      filter.commissionRate_max
    );
  }, [filter]);

  const columns = [
    {
      key: "downlineAgent",
      label: t("stockTransfer.agent"),
      render: (item) => item?.downlineAgent?.username || "-"
    },
    {
      key: "amount",
      label: t("stockTransfer.amount"),
      render: (item) => item?.amount ? `${parseFloat(item?.amount).toFixed(2)} AFN` : "-"
    },
    {
      key: "commissionRate",
      label: t("stockTransfer.commissionRate"),
      render: (item) => item?.commissionRate ? `${parseFloat(item?.commissionRate).toFixed(2)}%` : "-"
    },
    {
      key: "totalAmount",
      label: t("stockTransfer.totalAmount"),
      render: (item) => item?.totalAmount ? `${parseFloat(item?.totalAmount).toFixed(2)} AFN` : "-"
    },
    {
      key: "status",
      label: t("stockTransfer.status"),
      render: (item) => {
        const statusMap = {
          pending: { color: "warning", label: t('stockTransfer.statusPending') },
          approved: { color: "success", label: t('stockTransfer.statusApproved') },
          rejected: { color: "error", label: t('stockTransfer.statusRejected') },
          completed: { color: "info", label: t('stockTransfer.statusCompleted') },
        };
        const status = statusMap[item?.status] || { color: "light", label: item?.status || "-" };
        return (
          <Badge variant="light" color={status.color}>
            {status.label}
          </Badge>
        );
      }
    },
    {
      key: "createdAt",
      label: t("stockTransfer.createdAt"),
      render: (item) => item?.createdAt ? new Date(item.createdAt).toLocaleString() : "-"
    },
    {
      key: "actions",
      label: t("common2.actions"),
      render: (item) => (
        <button 
          onClick={() => {
            setSelectedTransaction(item);
            setReceiptOpen(true);
          }}
          className="text-primary-600 hover:text-primary-800 px-3 py-1 rounded hover:bg-primary-50 transition-colors"
        >
          {t("stockTransfer.viewReceipt")}
        </button>
      )
    }
  ];

  const responseData = data || {};
  const tableData = responseData?.status === "success" ? responseData?.data : [];
  
  const meta = data?.meta || {
    page: filter.page,
    pages: 1,
    total: 0,
    limit: filter.limit
  };

  const calculatedMeta = {
    ...meta,
    pages: Math.ceil((meta.total || 0) / filter.limit) || 1
  };

  return (
    <div className="w-full px-5 py-4 min-h-screen h-auto bg-[linear-gradient(135deg,_#EFF2FF_0%,_#FAF5FF_50%,_#FCF3FB_100%)] dark:bg-[linear-gradient(135deg,_#1e293b_0%,_#334155_50%,_#0f172a_100%)]">
      {isTransferModalOpen && (
        <TransferModal
          open={isTransferModalOpen}
          onClose={closeTransferModal}
          onSuccess={() => {
            closeTransferModal();
            refetch(); 
          }}
        />
      )}
      
      <StockTransferReceipt
        transaction={selectedTransaction} 
        isOpen={receiptOpen} 
        onClose={() => setReceiptOpen(false)} 
      />

      <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-5 mt-5">
        <div className="w-[70%] flex gap-3">
          <div className="w-full md:max-w-[500px] relative">
            <div className="relative bg-gradient-to-r from-[#F8F4FF] to-[#FAF9F3] dark:from-slate-800 dark:text-white dark:to-slate-800 rounded-md border border-[#E4E7EC] dark:border-gray-500">
              <input
                ref={searchRef}
                onChange={(e) => {
                  handleSetSearch(handleSearchChange, e.target.value, searchRef);
                }}
                type="text"
                placeholder={t("stockTransfer.searchPlaceholder")}
                defaultValue={filter.search}
                className="bg-transparent dark:placeholder-gray-200 h-[44px] pl-10 pr-8 transition-all duration-300 w-full outline-none px-3 py-2"
              />
              <Search
                size={18}
                className="absolute top-[12px] left-[8px] text-[#667085] dark:text-gray-300"
              />
              {filter.search && (
                <button
                  onClick={clearSearch}
                  className="absolute top-[12px] right-[8px] text-[#667085] dark:text-gray-300 hover:text-red-500"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          
          <button
            onClick={() => setIsDatePickerOpen(true)}
            className="max-w-[250px] cursor-pointer w-full md:w-auto border border-[#E4E7EC] rounded-md dark:border-gray-500 px-3 py-2 h-[44px] flex items-center justify-center gap-2 bg-gradient-to-r from-[#F8F4FF] to-[#FAF9F3] dark:from-slate-800 dark:text-white dark:to-slate-800 relative"
          >
            <ListFilter size={20} className="dark:text-gray-300 text-[#667085]" />
            <p className="dark:text-gray-300 text-[#667085] text-[14px] truncate">
              {filter['createdAt[gte]'] && filter['createdAt[lte]'] && formatDateRange()
                ? <span className="pr-5">{formatDateRange()}</span> 
                : t('stockTransfer.dateFilter')}
            </p>
            
            {filter['createdAt[gte]'] && filter['createdAt[lte]'] && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  clearDateFilter();
                }}
                className="absolute right-2 text-blue-800 dark:text-blue-200 hover:text-red-500"
              >
                <X size={16} />
              </button>
            )}
          </button>
          
          {/* <button
            onClick={() => setIsAdvanceFilterOpen(true)}
            className="max-w-[250px] cursor-pointer w-full md:w-auto border border-[#E4E7EC] rounded-md dark:border-gray-500 px-3 py-2 h-[44px] flex items-center justify-center gap-2 bg-gradient-to-r from-[#F8F4FF] to-[#FAF9F3] dark:from-slate-800 dark:text-gray-300 text-[#667085] text-[14px] dark:to-slate-800 relative"
          >
            <Filter size={20} className="dark:text-gray-300 text-[#667085]" />
            {t('stockTransfer.advanceFilter')}
            {isAnyFilterApplied && (
              <span className="absolute top-[-6px] right-[-6px] w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                <span className="text-white text-[10px]">!</span>
              </span>
            )}
          </button> */}
          
          {isAnyFilterApplied && (
            <button
              onClick={clearAllFilters}
              className="max-w-[150px] cursor-pointer w-full md:w-auto border border-red-500 rounded-md px-3 py-2 h-[44px] flex items-center justify-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
            >
              {t('stockTransfer.clearAll')}
            </button>
          )}
        </div>
        
        <div className="flex items-center justify-end w-full md:w-auto">
          <button
            onClick={() => {
              setIsTransferModalOpen(true);
            }}
            className="bg-[#CD0C02] px-4 py-[10px] rounded-md text-white hover:bg-[#CD0C02]/90 transition-colors font-medium"
          >
            {t("stockTransfer.transferAmount")}
          </button>
        </div>
      </div>

      <div className="mt-5">
        {!userInfo?.uid ? (
          <div className="w-full min-h-[200px] flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">{t("common2.loadingUser")}</p>
          </div>
        ) : (
          <ARHCustomDataTable
            data={tableData}
            meta={calculatedMeta}
            columns={columns}
            loading={isLoading || isFetching}
            isError={isError}
            error={isError ? (error || t("stockTransfer.errorLoading")) : null}
            onRetry={refetch}
            onPageChange={(page) => setFilter(prev => ({ ...prev, page }))}
            onLimitChange={(limit) => setFilter(prev => ({ ...prev, limit, page: 1 }))}
            searchQuery={filter.search}
            className="border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm"
            rowClassName="hover:bg-gray-50 dark:hover:bg-white/[0.05] border-b border-gray-100 dark:border-gray-800 last:border-b-0"
            emptyState={
              <div className="w-full min-h-[200px] flex flex-col items-center justify-center py-8">
                <img src="/images/Search.png" className="w-[120px] h-[120px] opacity-50 mb-4" alt="No data" />
                <p className="text-gray-500 dark:text-gray-400">
                  {filter.search 
                    ? t('stockTransfer.noResults', { search: filter.search })
                    : t("stockTransfer.noTransfersAvailable")}
                </p>
                <button
                  onClick={refetch}
                  className="mt-4 px-4 py-2 text-sm bg-[#CD0C02] text-white rounded hover:bg-[#CD0C02]/90"
                >
                  {t("common2.refresh")}
                </button>
              </div>
            }
            errorState={
              <div className="w-full min-h-[200px] flex flex-col items-center justify-center py-8">
                <img src="/images/Search.png" className="w-[120px] h-[120px] opacity-50 mb-4" alt="Error" />
                <div className="text-sm text-red-600 dark:text-red-400 mb-3">
                  {t("stockTransfer.errorLoading")}
                </div>
                <button
                  onClick={refetch}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  {t("common2.retry")}
                </button>
              </div>
            }
          />
        )}
      </div>

      {isDatePickerOpen && (
        <div 
          className="fixed inset-0 bg-black/40 bg-opacity-10 z-50 flex items-center justify-center p-4"
          onClick={() => setIsDatePickerOpen(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-xl max-w-full overflow-auto"
            onClick={e => e.stopPropagation()}
          >
            <ARHCustomDateFiler
              onApply={handleDateRangeApply} 
              onClose={() => setIsDatePickerOpen(false)} 
            />
          </div>
        </div>
      )}
      
      {/* {isAdvanceFilterOpen && (
        <ARHAdvanceFilterModal
          isOpen={isAdvanceFilterOpen}
          onClose={() => setIsAdvanceFilterOpen(false)}
          onApply={handleApplyAdvanceFilters}
          filterConfig={filterConfig}
          initialValues={initialAdvanceValues}
        />
      )} */}
    </div>
  );
}

export default StockTransferToDownlineAgents;